/**
 * Frontend types for AI question skipping. Mirrors the shapes returned by
 * GET /discovery/:companyId/:itemId/skip-plan and the category variant
 * (server: supabase/functions/server/discoverySkipPlanCore.ts).
 */

export type SkipAction = 'skip' | 'suggest' | 'ask';
export type SkipReason = 'already_answered' | 'condition_not_met' | 'inferred_from_context' | 'needs_input';
export type AiStatus = 'used' | 'disabled' | 'unavailable' | 'budget_exhausted' | 'no_context' | 'nothing_to_ask' | 'error';

export interface SkipEvidence {
  source: string;
  excerpt: string;
  verified: boolean;
}

export interface SkipDecision {
  questionId: string;
  action: SkipAction;
  reason: SkipReason;
  answer?: unknown;
  confidence: number;
  evidence?: SkipEvidence;
  decidedBy: 'rules' | 'ai';
}

export interface SkipPlan {
  decisions: SkipDecision[];
  aiStatus: AiStatus;
  aiDetail?: string;
  stats: { total: number; skipped: number; suggested: number; asked: number; answeredByRules: number; inferredByAi: number };
  contextSources: string[];
  generatedAt: string;
  cached?: boolean;
}

/** Human-readable note for the banner when the model did not run. */
export function describeAiStatus(status: AiStatus, detail?: string): string | null {
  switch (status) {
    case 'used':
    case 'nothing_to_ask':
      return null;
    case 'no_context':
      return 'No SOW or notes on file yet, so nothing could be pre-filled.';
    case 'disabled':
      return 'AI pre-fill is turned off for this company.';
    case 'budget_exhausted':
      return `AI pre-fill paused: ${detail ?? 'budget reached'}.`;
    case 'unavailable':
      return 'AI pre-fill is temporarily unavailable; all questions are shown.';
    default:
      return 'AI pre-fill could not run; all questions are shown.';
  }
}
